import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTRPC } from "../../lib/api";
import { Button, ErrorText, PageHeader, StatusText } from "../../ui";
import { ApprovalCard } from "./ApprovalCard";
import { reviewOf, type Approval } from "./model";
import { useApprovals, useMinuteClock, type ApprovalsTab } from "./use-approvals";
import { useDecide } from "./use-decide";

const TABS: Array<{ key: ApprovalsTab; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "history", label: "History" },
];

/**
 * `/approvals` (architecture §9): sends and destructive actions asked for from the CLI, MCP,
 * rules or AI wait here with their exact payload until the person approves (with a passkey) or
 * rejects them. History shows what was decided and how it went.
 */
export function ApprovalsPage() {
  const [tab, setTab] = useState<ApprovalsTab>("pending");
  const state = useApprovals(tab);
  const now = useMinuteClock();
  const { approve, reject, busyId } = useDecide();
  const addressOf = useAccountAddresses(state.status === "ready" ? state.approvals : []);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Approvals" />
      <p className="text-sm text-muted">
        Emails and destructive changes asked for by the command line, an agent, a rule or AI wait here. Nothing happens until you approve.
      </p>
      <div role="group" aria-label="Show" className="flex gap-2">
        {TABS.map((t) => (
          <Button key={t.key} variant={tab === t.key ? "primary" : "secondary"} aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </Button>
        ))}
      </div>

      {state.status === "loading" ? <StatusText>Loading…</StatusText> : null}
      {state.status === "error" ? <ErrorText>Could not load approvals: {state.message}</ErrorText> : null}
      {state.status === "ready" && state.approvals.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted">
          {tab === "pending" ? "Nothing is waiting for your approval." : "No decisions yet."}
        </p>
      ) : null}
      {state.status === "ready" ? (
        <ul aria-label={tab === "pending" ? "Pending approvals" : "Decided approvals"} className="flex flex-col gap-4">
          {state.approvals.map((a) => (
            <li key={a.id}>
              <ApprovalCard
                approval={a}
                review={reviewOf(a, addressOf)}
                now={now}
                actions={
                  tab === "pending"
                    ? { onApprove: () => void approve(a.id), onReject: () => void reject(a.id), busy: busyId === a.id }
                    : null
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Connected accounts' addresses, fetched only when a send is on screen (to show "From"). */
function useAccountAddresses(approvals: readonly Approval[]): (accountId: string) => string | null {
  const trpc = useTRPC();
  const needed = approvals.some((a) => a.action === "mail.send");
  const accounts = useQuery({ ...trpc.accounts.list.queryOptions(), enabled: needed });
  return (accountId) => {
    const found = accounts.data?.find((a) => a.id === accountId);
    return found === undefined ? null : found.address;
  };
}
